/**
 * DEV-648 — botões flutuantes de terceiros (suporte/Intercom e VLibras) acima da barra de
 * ações do rodapé dos formulários.
 *
 * O launcher do Intercom fica em `bottom: 20px; right: 20px` no celular e cobria a metade de
 * baixo do "Avançar" (a barra de ações repousa a `py-12` do fim da tela e o botão é alinhado à
 * direita). A barra NÃO é fixa — rola com o conteúdo —, então a posição que importa é a de
 * repouso: conteúdo curto, ou rolado até o fim, que é onde o candidato toca.
 *
 * Regra: enquanto houver uma barra de ações de formulário na tela (`[data-slot="form-footer"]`)
 * que repousa na faixa do launcher, o `<html>` ganha `data-form-footer` e a variável
 * `--form-footer-reserve` (px) = topo da barra em repouso, medido do rodapé da janela, + folga.
 * O CSS global (`index.css`) sobe o launcher do Intercom por essa variável. Sem barra, nada é
 * tocado e o Intercom fica na posição de hoje (painel: 96/24 no desktop, 20/20 no celular).
 *
 * Por que não `Intercom('update', { vertical_padding })`: não há como voltar ao espaçamento do
 * painel ao sair do formulário, a doc limita `update` a 20 chamadas por 30 min e o slot de QA
 * sobe o SPA sem Intercom — a regra precisa ser conferível sem ele.
 *
 * O VLibras (loader v7, `top: calc(50vh - 20px)` à direita) já fica acima da barra e afastado do
 * launcher subido; não é tocado.
 *
 * Nunca lança: qualquer falha deixa o comportamento de hoje.
 */

export const FORM_FOOTER_SELECTOR = '[data-slot="form-footer"]';
export const FORM_FOOTER_ATTR = 'data-form-footer';
export const FORM_FOOTER_RESERVE_VAR = '--form-footer-reserve';
/** Folga entre o launcher subido e o topo da barra em repouso. */
export const RESERVE_GAP_PX = 8;
/**
 * Até esta distância do rodapé da janela a barra em repouso pode encontrar o launcher
 * (20 + 48 no celular; 96 + 48 no desktop). Barra mais alta que isso não precisa de regra.
 */
export const LAUNCHER_ZONE_MAX_PX = 160;

export interface RestGeometry {
  innerHeight: number;
  /** Retângulo atual da barra (viewport). */
  footer: { top: number; bottom: number; height: number };
  /** O rolador da barra: o documento ou um ancestral com overflow rolável. */
  scroller: { top: number; bottom: number; scrollTop: number; scrollHeight: number };
}

/**
 * Topo da barra em repouso (rolagem no fim), medido do rodapé da janela. Não depende da
 * posição atual de rolagem: o espaço abaixo da barra no fim do conteúdo é fixo.
 */
export function restTopFromGeometry(g: RestGeometry): number {
  const footerBottomInScroller = g.footer.bottom - g.scroller.top + g.scroller.scrollTop;
  const spaceBelow = g.scroller.scrollHeight - footerBottomInScroller;
  const scrollerToWindowBottom = g.innerHeight - g.scroller.bottom;
  return scrollerToWindowBottom + spaceBelow + g.footer.height;
}

/** Reserva (px) para o launcher, ou `null` quando a barra não alcança a faixa dele. */
export function computeReserve(restTop: number): number | null {
  if (!Number.isFinite(restTop)) return null;
  const top = Math.max(0, restTop);
  if (top > LAUNCHER_ZONE_MAX_PX) return null;
  return Math.ceil(top + RESERVE_GAP_PX);
}

function isScrollable(el: Element): boolean {
  const { overflowY } = getComputedStyle(el);
  return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
}

/** Ancestral rolável mais próximo, ou `null` quando é o documento que rola. */
function findScroller(el: Element): HTMLElement | null {
  let node: Element | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollable(node)) return node as HTMLElement;
    node = node.parentElement;
  }
  return null;
}

/** Mede a barra no DOM real (layout). Substituível nos testes. */
export function measureFooterRest(footer: HTMLElement): number {
  const rect = footer.getBoundingClientRect();
  const scroller = findScroller(footer);
  if (scroller) {
    const sr = scroller.getBoundingClientRect();
    return restTopFromGeometry({
      innerHeight: window.innerHeight,
      footer: { top: rect.top, bottom: rect.bottom, height: rect.height },
      scroller: {
        top: sr.top,
        bottom: sr.bottom,
        scrollTop: scroller.scrollTop,
        scrollHeight: scroller.scrollHeight,
      },
    });
  }
  const doc = document.scrollingElement ?? document.documentElement;
  return restTopFromGeometry({
    innerHeight: window.innerHeight,
    footer: { top: rect.top, bottom: rect.bottom, height: rect.height },
    // O documento rola "por dentro": a sua caixa é a janela (top 0, bottom innerHeight) e a
    // rolagem entra só em scrollTop — nunca também no top, senão scrollY conta duas vezes.
    scroller: {
      top: 0,
      bottom: window.innerHeight,
      scrollTop: window.scrollY,
      scrollHeight: doc.scrollHeight,
    },
  });
}

function isRendered(el: Element): boolean {
  return el.getClientRects().length > 0 || (el as HTMLElement).offsetParent !== null;
}

/** A barra que vale: a última renderizada (telas empilhadas mostram a de cima por último). */
function activeFooter(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(FORM_FOOTER_SELECTOR);
  for (let i = all.length - 1; i >= 0; i -= 1) {
    const el = all[i];
    if (el && isRendered(el)) return el;
  }
  return null;
}

export function applyFormFooterState(
  reserve: number | null,
  root = document.documentElement
): void {
  if (reserve === null) {
    root.removeAttribute(FORM_FOOTER_ATTR);
    root.style.removeProperty(FORM_FOOTER_RESERVE_VAR);
    return;
  }
  root.setAttribute(FORM_FOOTER_ATTR, '');
  root.style.setProperty(FORM_FOOTER_RESERVE_VAR, `${reserve}px`);
}

export interface InitFloatingLaunchersOptions {
  /** Medidor da barra (topo em repouso, do rodapé da janela). Injetável nos testes. */
  measure?: (footer: HTMLElement) => number;
}

/**
 * Liga a regra: sincroniza no boot, a cada mutação do DOM (coalescida por frame) e no
 * redimensionamento/rotação. Devolve a função que desliga tudo.
 */
export function initFloatingLaunchers({
  measure = measureFooterRest,
}: InitFloatingLaunchersOptions = {}): () => void {
  let frame = 0;

  const sync = () => {
    frame = 0;
    try {
      const footer = activeFooter();
      applyFormFooterState(footer ? computeReserve(measure(footer)) : null);
    } catch (err) {
      // Medida impossível (DOM estranho): comportamento de hoje, sem erro não tratado.
      applyFormFooterState(null);
      console.warn('[floating-launchers] sync skipped', err);
    }
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(sync);
  };

  let observer: MutationObserver | null = null;
  try {
    observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-slot'],
    });
  } catch (err) {
    console.warn('[floating-launchers] observer unavailable', err);
  }
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);

  sync();

  return () => {
    observer?.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
}
